/**
 * Shared property-extraction helpers for the guest-funnel PostHog events
 * (search_intent, search_performed, property_card_clicked, listing_dates_selected,
 * booking_initiated, confirm_and_pay, booking_confirmed).
 *
 * Each event-firing site imports the helper it needs so the property shape
 * stays consistent across sites and PRD §6/§7 enums live in one place.
 */

import bookingStore from '../../stores/booking-store.js'
import { trackConversionEvent } from '../../utilities/use-analytics.js'

const PURCHASE_TRACKED_KEY_PREFIX = 'lodgr_purchase_tracked_'

/**
 * Current selected currency code, or undefined if not set. Centralized so
 * every event in this funnel reads from the same place.
 */
export const currentCurrency = () => bookingStore.get('selectedCurrencyCode') || undefined

/**
 * Build the property payload for `property_card_clicked` from a property
 * object as returned by the search / homepage API.
 *
 * `is_inventory_loaded`: true when the card has a date-range price estimate
 * (`pricing_info.price_estimate`); false when only the fallback `avg_nightly`
 * is present or no price at all. This matches the user-visible distinction
 * between "card shows real total for searched dates" vs. "card shows generic
 * nightly average / skeleton".
 *
 * @param {object} property - Property record from search/home API.
 * @param {'search_primary' | 'search_secondary' | 'home_carousel'} source
 * @returns {object}
 */
export const propertyCardEventProperties = (property, source) => {
    const pricingInfo = property?.pricing_info ?? {}
    const ratingInfo = property?.rating_info ?? {}
    const price = pricingInfo.price_estimate ?? pricingInfo.avg_nightly ?? null

    return {
        city: property?.city ?? null,
        currency: pricingInfo.currency ?? currentCurrency() ?? null,
        is_inventory_loaded: Boolean(pricingInfo.price_estimate),
        price,
        property_id: property?.property_id ?? null,
        property_name: property?.name ?? null,
        rating: ratingInfo.rating ?? null,
        source,
    }
}

/**
 * Map the internal payment-method ID used by the checkout page to the
 * canonical PRD §7 `booking_method` enum value.
 *
 * @param {string} paymentMethodId - One of:
 *   'stripe-card' | 'credit-card' | 'verto-wallet' | 'buy-now-pay-later' |
 *   'direct-to-host-payment'.
 * @returns {string} PRD enum value, or 'other' for any unmapped id.
 */
export const bookingMethodFromPaymentMethodId = (paymentMethodId) => {
    switch (paymentMethodId) {
        case 'stripe-card': {
            return 'stripe'
        }
        case 'buy-now-pay-later': {
            return 'bnpl'
        }
        case 'verto-wallet': {
            return 'verto_wallet'
        }
        case 'credit-card': {
            return 'credit_card'
        }
        case 'direct-to-host-payment': {
            return 'direct_to_host'
        }
        default: {
            return 'other'
        }
    }
}

/**
 * Fire the GA4/GTM `purchase` event exactly once per booking.
 *
 * The PostHog `booking_confirmed` event deliberately refires on every
 * confirmation-page mount (acceptable for dashboards — see the comment at
 * its call site). A `purchase` fed to GA4/GTM — and any Ads conversion tag
 * built on it — must NOT count reloads or revisits, so this is gated by a
 * per-booking localStorage marker, same pattern as
 * `markUserBecameHostIfFirstTime` in analytics-host-flow.js.
 *
 * Named `purchase` on purpose: it is the GA4 ecommerce event this property
 * already has configured as its only key event (previously never received),
 * so the payload follows the ecommerce shape (transaction_id/value/currency/
 * items). `total_nights` rides along as a custom param — nights-as-quantity
 * would misread in ecommerce reports.
 *
 * @param {object} booking - Loaded booking record (booking_id, total,
 *   currency, property_id, total_nights).
 */
export const trackPurchaseOnceForBooking = (booking) => {
    const bookingId = booking?.booking_id
    if (!bookingId) return
    if (typeof window === 'undefined' || !window.localStorage) return

    const storageKey = `${PURCHASE_TRACKED_KEY_PREFIX}${bookingId}`
    try {
        if (window.localStorage.getItem(storageKey) === 'true') return
        window.localStorage.setItem(storageKey, 'true')
    } catch {
        // Storage read/write failed — fire the event anyway so the
        // conversion isn't lost, accepting a possible duplicate in this
        // rare path (same trade-off as analytics-host-flow.js).
    }

    const total = Number(booking.total)

    trackConversionEvent('purchase', {
        parameters: {
            currency: booking.currency,
            items: [
                {
                    item_id: booking.property_id,
                },
            ],
            total_nights: booking.total_nights,
            transaction_id: bookingId,
            ...(Number.isFinite(total) ? {
                value: total,
            } : {}),
        },
    })
}
